import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Archive, Lock, Trash2, X } from 'lucide-react';
import {
  anonymizeCustomer,
  archiveCustomer,
  bulkArchiveCustomers,
  bulkDeleteCustomers,
  deleteCustomer,
  getCustomerReferences,
  type BulkArchiveResult,
  type BulkDeleteResult,
  type CustomerReferences,
} from '@/lib/api/customers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { cn } from '@/lib/utils.js';

function fullName(c: { firstName: string; lastName?: string | null }) {
  return [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
}

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

type CustomerLite = { id: string; firstName: string; lastName?: string | null; totalTickets?: number };

export function ArchiveCustomerModal({
  open,
  customer,
  onClose,
  onDone,
}: {
  open: boolean;
  customer: CustomerLite | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const refsQuery = useQuery({
    queryKey: ['customer-refs', customer?.id],
    queryFn: () => getCustomerReferences(customer!.id),
    enabled: open && Boolean(customer?.id),
  });

  useEffect(() => {
    if (open) setReason('');
  }, [open, customer?.id]);

  const mut = useMutation({
    mutationFn: () => archiveCustomer(customer!.id, { reason: reason.trim() || null }),
    onSuccess: () => {
      toastSuccess('Customer archived');
      onDone();
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open || !customer) return null;

  const name = fullName(customer);
  const refs = refsQuery.data;
  const blocked = Boolean(refs?.hasOpenTickets);

  return (
    <ModalShell title={blocked ? `Cannot archive ${name}` : `Archive ${name}?`} onClose={onClose}>
      {refsQuery.isLoading ? (
        <p className="text-sm text-slate-500">Checking open tickets…</p>
      ) : blocked ? (
        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            This customer has {refs!.openTickets.length} open ticket
            {refs!.openTickets.length === 1 ? '' : 's'}:
          </p>
          <ul className="max-h-40 space-y-1 overflow-auto rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
            {refs!.openTickets.map((t) => (
              <li key={t.id}>
                {t.ticketNumber} — {formatStatus(t.status)}
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-600">Close or reassign these first.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
              onClick={onClose}
            >
              Cancel
            </button>
            <a
              href={`/tickets?customerId=${customer.id}`}
              className="rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white"
            >
              View Tickets
            </a>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            They will be hidden from search and lists. All{' '}
            {customer.totalTickets ?? refs?.byTable.tickets ?? 0} tickets and history are preserved.
          </p>
          <label className="block text-sm">
            <span className="text-slate-600">Reason (optional)</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Duplicate record"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={mut.isPending}
              onClick={() => mut.mutate()}
            >
              <Archive className="h-4 w-4" />
              Archive
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

export function DeleteCustomerModal({
  open,
  customer,
  onClose,
  onDone,
  onArchiveInstead,
}: {
  open: boolean;
  customer: CustomerLite | null;
  onClose: () => void;
  onDone: () => void;
  onArchiveInstead?: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  const refsQuery = useQuery({
    queryKey: ['customer-refs', customer?.id],
    queryFn: () => getCustomerReferences(customer!.id),
    enabled: open && Boolean(customer?.id),
  });

  useEffect(() => {
    if (open) {
      setReason('');
      setConfirmName('');
    }
  }, [open, customer?.id]);

  const mut = useMutation({
    mutationFn: (managerOverrideAuthorizedBy: string) =>
      deleteCustomer(customer!.id, {
        reason: reason.trim(),
        confirmName: confirmName.trim(),
        managerOverrideAuthorizedBy,
      }),
    onSuccess: () => {
      toastSuccess('Customer permanently deleted');
      onDone();
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open || !customer) return null;

  const name = fullName(customer);
  const refs = refsQuery.data;
  const blocked = refs != null && !refs.canHardDelete;
  const nameOk = confirmName.trim().toLowerCase() === name.toLowerCase();
  const reasonOk = reason.trim().length >= 3;

  return (
    <>
      <ModalShell
        title={blocked ? `Cannot delete ${name}` : `Permanently delete ${name}?`}
        onClose={onClose}
      >
        {refsQuery.isLoading ? (
          <p className="text-sm text-slate-500">Checking references…</p>
        ) : blocked ? (
          <div className="space-y-3">
            <p className="flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              This customer is referenced by:
            </p>
            <ul className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
              {Object.entries(refs!.byTable).map(([table, n]) => (
                <li key={table}>
                  {n} {table.replace(/_/g, ' ')}
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-600">
              Deleting would corrupt financial records.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700" onClick={onClose}>
                Cancel
              </button>
              {onArchiveInstead ? (
                <button
                  type="button"
                  className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => {
                    onClose();
                    onArchiveInstead();
                  }}
                >
                  Archive Instead
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="flex items-start gap-2 text-sm text-red-800">
              <Trash2 className="mt-0.5 h-4 w-4 shrink-0" />
              No tickets, payments, or history found. This cannot be undone.
            </p>
            <label className="block text-sm">
              <span className="text-slate-600">Type the customer&apos;s name to confirm</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={name}
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Reason</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Empty import stub"
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!nameOk || !reasonOk || mut.isPending}
                onClick={async () => {
                  const result = await requireOverride({
                    action: 'delete_customer',
                    title: 'Manager Approval to Delete Customer',
                    description: `Permanently deleting ${name}`,
                    requiredRole: 'tenant_admin',
                  });
                  if (!result.approved) return;
                  mut.mutate(result.authorizedBy.id);
                }}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        )}
      </ModalShell>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </>
  );
}

export function AnonymizeCustomerModal({
  open,
  customer,
  onClose,
  onDone,
}: {
  open: boolean;
  customer: CustomerLite | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  useEffect(() => {
    if (open) {
      setReason('');
      setConfirmText('');
    }
  }, [open, customer?.id]);

  const mut = useMutation({
    mutationFn: (managerOverrideAuthorizedBy: string) =>
      anonymizeCustomer(customer!.id, {
        reason: reason.trim(),
        confirmText: 'ANONYMIZE',
        managerOverrideAuthorizedBy,
      }),
    onSuccess: () => {
      toastSuccess('Customer anonymized');
      onDone();
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open || !customer) return null;

  const name = fullName(customer);
  const canSubmit = confirmText.trim() === 'ANONYMIZE' && reason.trim().length >= 3;

  return (
    <>
      <ModalShell title={`Anonymize ${name}?`} onClose={onClose}>
        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm text-slate-700">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            Replaces name, email, phone and address with placeholders. Tickets, payments and totals
            are preserved and remain linked. This cannot be undone.
          </p>
          <label className="block text-sm">
            <span className="text-slate-600">Type ANONYMIZE to confirm</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">Reason</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!canSubmit || mut.isPending}
              onClick={async () => {
                const result = await requireOverride({
                  action: 'anonymize_customer',
                  title: 'Manager Approval to Anonymize Customer',
                  description: `Anonymizing ${name}`,
                  requiredRole: 'tenant_admin',
                });
                if (!result.approved) return;
                mut.mutate(result.authorizedBy.id);
              }}
            >
              Anonymize
            </button>
          </div>
        </div>
      </ModalShell>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </>
  );
}

export function BulkArchiveModal({
  open,
  customerIds,
  onClose,
  onDone,
}: {
  open: boolean;
  customerIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<BulkArchiveResult | null>(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setReason('');
      return;
    }
    let cancelled = false;
    void bulkArchiveCustomers({ customerIds, dryRun: true })
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((error) => {
        if (!cancelled) toastError(parseApiError(error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [open, customerIds]);

  const mut = useMutation({
    mutationFn: () =>
      bulkArchiveCustomers({
        customerIds,
        reason: reason.trim() || null,
        dryRun: false,
      }),
    onSuccess: (res) => {
      toastSuccess(`Archived ${res.archived.length} customer${res.archived.length === 1 ? '' : 's'}`);
      onDone();
      onClose();
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  if (!open) return null;

  const willArchive = preview?.archived.length ?? 0;
  const skipped = preview?.skipped ?? [];

  return (
    <ModalShell title={`Archive ${customerIds.length} customers?`} onClose={onClose}>
      {!preview ? (
        <p className="text-sm text-slate-500">Checking eligibility…</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-emerald-800">✓ {willArchive} will be archived</p>
          {skipped.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
              <p className="font-medium text-amber-900">
                ⚠ {skipped.length} skipped — open tickets:
              </p>
              <p className="mt-1 text-amber-800">
                {skipped.map((s) => s.name).join(', ')}
              </p>
            </div>
          ) : null}
          <label className="block text-sm">
            <span className="text-slate-600">Reason</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={willArchive === 0 || mut.isPending}
              onClick={() => mut.mutate()}
            >
              Archive {willArchive} Customer{willArchive === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

export function BulkDeleteModal({
  open,
  customerIds,
  onClose,
  onDone,
}: {
  open: boolean;
  customerIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [preview, setPreview] = useState<BulkDeleteResult | null>(null);
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setReason('');
      setConfirmText('');
      return;
    }
    let cancelled = false;
    void bulkDeleteCustomers({
      customerIds,
      reason: 'preview',
      confirmText: 'DELETE',
      dryRun: true,
    })
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((error) => {
        if (!cancelled) toastError(parseApiError(error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [open, customerIds]);

  const mut = useMutation({
    mutationFn: (managerOverrideAuthorizedBy: string) =>
      bulkDeleteCustomers({
        customerIds,
        reason: reason.trim(),
        confirmText: 'DELETE',
        managerOverrideAuthorizedBy,
        dryRun: false,
      }),
    onSuccess: (res) => {
      toastSuccess(`Deleted ${res.deleted.length}; skipped ${res.skipped.length}`);
      onDone();
      onClose();
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  if (!open) return null;

  const willDelete = preview?.deleted.length ?? 0;
  const skipped = preview?.skipped ?? [];
  const canSubmit = confirmText === 'DELETE' && reason.trim().length >= 3 && willDelete > 0;

  return (
    <>
      <ModalShell title={`Permanently delete ${customerIds.length} customers?`} onClose={onClose}>
        {!preview ? (
          <p className="text-sm text-slate-500">Checking references…</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-emerald-800">
              ✓ {willDelete} with zero references can be deleted
            </p>
            {skipped.length > 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                ⚠ {skipped.length} skipped (have linked records):{' '}
                {skipped.map((s) => s.name).join(', ')}
              </div>
            ) : null}
            <label className="block text-sm">
              <span className="text-slate-600">Type DELETE to confirm</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Reason</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!canSubmit || mut.isPending}
                onClick={async () => {
                  const result = await requireOverride({
                    action: 'bulk_delete_customers',
                    title: 'Manager Approval to Delete Customers',
                    description: `Permanently deleting ${willDelete} customer(s)`,
                    requiredRole: 'tenant_admin',
                  });
                  if (!result.approved) return;
                  mut.mutate(result.authorizedBy.id);
                }}
              >
                Delete {willDelete}
              </button>
            </div>
          </div>
        )}
      </ModalShell>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className={cn('w-full max-w-md rounded-2xl bg-white p-5 shadow-xl')}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button type="button" className="rounded p-1 text-slate-400 hover:bg-slate-100" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function useCustomerRefs(customerId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['customer-refs', customerId],
    queryFn: () => getCustomerReferences(customerId!),
    enabled: enabled && Boolean(customerId),
  });
}

export type { CustomerReferences };
